import * as React from 'react';

import {
  enumPurchaseOrderStatus,
  enumUnit,
  enumWeightDisplayUnit,
  Unit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, ContentCard } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftLineItemsById,
  $effectiveLineItemsById,
  $hasDraftChangesById,
  $purchaseOrderDetailById,
  $updatePurchaseOrder,
} from '../../_state/purchaseOrderDetail.state';
import { $purchaseOrderIsMissingRequired } from '../../_state/purchaseOrderForm.state';
import { useWeightUnitManager } from '../../hooks/useWeightUnitManager';
import { PurchaseOrderMaterialsTable } from '../_shared/LineItems';
import { WeightUnitSwitch } from '../_shared/WeightUnitSwitch';

import { convertToPurchaseOrderLoadFormLineItem } from './helpers/convertPurchaseOrderToLoadLineItem';
import { validateLineItems } from './helpers/validateLineItems';
import { usePurchaseOrderMaterialsErrors } from './hooks/usePurchaseOrderMaterialsErrors';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import {
  enumLoadWeightType,
  LoadFormLineItem,
} from '~/modules/trade/types/load';
import { PurchaseOrderLineItemRecord } from '~/modules/trade/types/purchaseOrder';
import {
  calculateTotalPrice,
  toMoneyInput,
  zeroMoney,
} from '~/modules/trade/utils/money';

type Props = {
  purchaseOrderId: string;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

export const PurchaseOrderTabMaterials: React.FC<Props> = ({
  purchaseOrderId,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();
  const toast = useToast();
  const updatePurchaseOrder = useSetAtom($updatePurchaseOrder);

  const purchaseOrderData = useAtomValue(
    $purchaseOrderDetailById(purchaseOrderId),
  );
  const purchaseOrder = purchaseOrderData?.data;

  const isMissingRequired = useAtomValue($purchaseOrderIsMissingRequired);
  const isDraftStatus = purchaseOrder?.status === enumPurchaseOrderStatus.Draft;

  const effectiveLineItems = useAtomValue(
    $effectiveLineItemsById(purchaseOrderId),
  );
  const setDraftLineItems = useSetAtom(
    $currentDraftLineItemsById(purchaseOrderId),
  );
  const hasDraftChanges = useAtomValue($hasDraftChangesById(purchaseOrderId));

  const [lineItemErrors, setLineItemErrors] = React.useState<
    Record<string, string>
  >({});

  const [isSaving, setIsSaving] = React.useState(false);

  const [localWeightDisplayUnit, setLocalWeightDisplayUnit] =
    React.useState<WeightDisplayUnit | null>(null);

  React.useEffect(() => {
    if (purchaseOrder?.weightDisplayUnit !== undefined) {
      setLocalWeightDisplayUnit(purchaseOrder.weightDisplayUnit ?? null);
    }
  }, [purchaseOrder?.weightDisplayUnit]);

  const hasWeightDisplayUnitChanges = React.useMemo(
    () =>
      purchaseOrder?.weightDisplayUnit &&
      localWeightDisplayUnit !==
        (purchaseOrder?.weightDisplayUnit ?? enumWeightDisplayUnit.ScaleUnit),
    [localWeightDisplayUnit, purchaseOrder?.weightDisplayUnit],
  );

  const handleWeightDisplayUnitChange = React.useCallback(
    (unit: WeightDisplayUnit) => {
      setLocalWeightDisplayUnit(unit);
    },
    [],
  );

  const materialsErrors = usePurchaseOrderMaterialsErrors(lineItemErrors);

  const { currentWeightUnit, handleWeightUnitChange } = useWeightUnitManager({
    weightDisplayUnitPreference: localWeightDisplayUnit,
    onWeightDisplayUnitChange: handleWeightDisplayUnitChange,
    defaultUnit: 'PriceUnit',
  });

  const handleAddMaterial = React.useCallback(() => {
    const newLineItem: PurchaseOrderLineItemRecord = {
      id: `temp-${Date.now()}-${Math.random()}`,
      itemId: '',
      quantity: 0,
      unit: enumUnit.Pounds,
      unitPrice: zeroMoney(),
      totalPrice: zeroMoney(),
    };
    setDraftLineItems([...effectiveLineItems, newLineItem]);
  }, [effectiveLineItems, setDraftLineItems]);

  const handleUpdateLineItem = React.useCallback(
    (lineItemId: string, updates: Partial<LoadFormLineItem>) => {
      const index = effectiveLineItems.findIndex(
        (item) => item.id === lineItemId,
      );
      if (index === -1) return;

      const updatedItems = [...effectiveLineItems];
      const currentItem = updatedItems[index];
      const updatedItem: PurchaseOrderLineItemRecord = { ...currentItem };

      if (updates.itemId !== undefined) {
        updatedItem.itemId = updates.itemId;
      }

      if (
        updates.weights !== undefined &&
        updates.weights?.[enumLoadWeightType.Net] !== null
      ) {
        updatedItem.quantity = updates.weights?.[enumLoadWeightType.Net]?.value;
        updatedItem.unit = updates.weights?.[enumLoadWeightType.Net]?.unit;
      }

      if (updates.unitPrice !== undefined && updates.unitPrice !== null) {
        updatedItem.unitPrice = updates.unitPrice;
      }

      if (
        updates.weights?.[enumLoadWeightType.Net] !== undefined ||
        updates.unitPrice !== undefined
      ) {
        updatedItem.totalPrice = calculateTotalPrice(
          updatedItem.unitPrice,
          updatedItem.quantity,
        );
      }

      updatedItems[index] = updatedItem;

      setDraftLineItems(updatedItems);
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDeleteLineItem = React.useCallback(
    (lineItemId: string) => {
      const filteredItems = effectiveLineItems.filter(
        (item) => item.id !== lineItemId,
      );
      setDraftLineItems(filteredItems);
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDiscard = React.useCallback(() => {
    setDraftLineItems(null);
  }, [setDraftLineItems]);

  const handleSave = React.useCallback(async () => {
    if (!purchaseOrder?.id || isSaving) return;

    const errors = validateLineItems(effectiveLineItems);

    if (Object.keys(errors).length > 0) {
      setLineItemErrors(errors);
      toast.error(t.formErrors.purchaseOrders.materials.errors);
      return;
    }

    setIsSaving(true);
    try {
      const validLineItems = effectiveLineItems.filter(
        (item) =>
          item.itemId &&
          item.itemId.trim() !== '' &&
          item.quantity !== null &&
          item.quantity > 0 &&
          item.unit &&
          item.unitPrice,
      );

      await updatePurchaseOrder({
        id: purchaseOrder.id,
        // weightDisplayUnit: localWeightDisplayUnit,
        lineItems: validLineItems.map((item) => ({
          id: item.id.startsWith('temp-') ? undefined : item.id,
          itemId: item.itemId,
          quantity: item.quantity,
          unit: item.unit as Unit,
          unitPrice: toMoneyInput(item.unitPrice),
          totalPrice: toMoneyInput(item.totalPrice),
        })),
      });
      toast.success(t.purchaseOrderDetail.messages.materialsUpdated);
    } catch (error) {
      toast.error(
        `Failed to update materials: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    purchaseOrder?.id,
    effectiveLineItems,
    isSaving,
    updatePurchaseOrder,
    toast,
    t,
  ]);

  const loadFormLineItems = React.useMemo(
    () => effectiveLineItems.map(convertToPurchaseOrderLoadFormLineItem),
    [effectiveLineItems],
  );

  if (!purchaseOrder) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 p-6">
          <div className="rounded-lg bg-white p-6">
            <div className="py-8 text-center">
              <p className="text-neutral/50">No purchase order selected</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        '@PurchaseOrderTabMaterials flex flex-col gap-5',
        variant !== 'embedded' && 'p-5',
      )}
    >
      <ContentCard
        className={cn(
          'p-0',
          variant === 'embedded' && 'rounded-none border-none',
        )}
      >
        <div className="flex-1">
          {loadFormLineItems.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mb-6 text-neutral/50">
                <p className="mb-2 text-lg">
                  No materials added to this purchase order
                </p>
                <p className="text-sm">
                  Materials can be added when creating or editing the purchase
                  order.
                </p>
              </div>
              {!disabled && (
                <Button
                  variant="outline"
                  color="primary"
                  onPress={handleAddMaterial}
                  size="large"
                >
                  {t.purchaseOrderDetail.actions.addMaterial}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex">
              <div className="px-7.5 pt-4 pb-6">
                <div className="mb-4 flex justify-between">
                  <h3 className="text-lg font-semibold">
                    {t.itemTable.purchaseOrderTitle}
                  </h3>

                  <div className="flex justify-end gap-2">
                    <WeightUnitSwitch
                      value={currentWeightUnit}
                      onChange={handleWeightUnitChange}
                    />
                  </div>
                </div>
                <PurchaseOrderMaterialsTable
                  lineItems={loadFormLineItems}
                  onUpdateLineItem={handleUpdateLineItem}
                  onDeleteLineItem={handleDeleteLineItem}
                  onAddLineItem={handleAddMaterial}
                  disabled={disabled || isSaving}
                  weightUnit={currentWeightUnit}
                  errors={materialsErrors}
                />
              </div>
            </div>
          )}
        </div>
        {!disabled && (hasDraftChanges || hasWeightDisplayUnitChanges) && (
          <div className="sticky bottom-0 border-t border-neutral/10 bg-white px-6 py-4">
            <div className="flex items-center justify-end">
              <div className="flex gap-3">
                <Button
                  color="neutral"
                  variant="outline"
                  onPress={handleDiscard}
                  isDisabled={isSaving}
                >
                  {t.purchaseOrderDetail.actions.discard}
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleSave}
                  isPending={isSaving}
                  isLoading={isSaving}
                  isDisabled={!hasDraftChanges && !hasWeightDisplayUnitChanges}
                >
                  {isMissingRequired && isDraftStatus
                    ? t.loadDetail.footer.saveDraft
                    : t.purchaseOrderDetail.actions.saveUpdates}
                </Button>
              </div>
            </div>
          </div>
        )}
      </ContentCard>
    </div>
  );
};
